/* Copyright 2023 (Unpublished) Verto Inc. */

// Angular
import { Injectable } from '@angular/core';

// rxjs
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TimeZoneService {
  private timezoneSource: BehaviorSubject<string>;

  private timezoneOptions = [
    { name: 'America/Toronto', value: 'America/Toronto' },
    { name: 'UTC', value: 'UTC' },
  ];

  public timezone: Observable<string>;

  constructor() {
    this.timezoneSource = new BehaviorSubject<string>(this.getStoredTimezone());
    this.timezone = this.timezoneSource.asObservable();

    // Ensure that user's current timezone is in the options
    let inOptions = false;
    const currentTimezone = this.getCurrentTimezone();
    for (let i = 0; i < this.timezoneOptions.length; i++) {
      if (this.timezoneOptions[i].value === currentTimezone) {
        return;
      }
    }
    this.timezoneOptions.push({
      name: currentTimezone,
      value: currentTimezone,
    });
  }

  /**
   * Call this method when the timezone is changed, it will update
   * the timezone preference in localstorage and also allow components
   * to notice the change.
   *
   * @param tz New timezone
   */
  timezoneChange(tz: string) {
    this.storeTimezone(tz);
    this.timezoneSource.next(tz);
  }

  /**
   * Get a list of available timezones in format `{name, value}`
   */
  getTimezoneOptions(): { name: string; value: string }[] {
    return [
      { name: 'America/Toronto', value: 'America/Toronto' },
      { name: 'UTC', value: 'UTC' },
    ];
  }

  /**
   * Get user's current timezone as string.
   */
  getCurrentTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Get the timezone that is stored in the localstorage. If not found
   * it will return user's current timezone instead.
   * Return null if not found.
   */
  getStoredTimezone(): string {
    const tz = localStorage.getItem('tz');
    if (!tz) {
      return this.getCurrentTimezone();
    }
    return tz;
  }

  /**
   *
   * Store timezone into the localstorage.
   *
   * @param tz Timezone in string, currently support `UTC`, `America/Toronto`
   */
  storeTimezone(tz: string): void {
    localStorage.setItem('tz', tz);
  }
}
